/**
 * `buildViewModel(state, register)` — the pure presentation builder (Deliverable A
 * + the register/precision-loss plumbing of Deliverable D).
 *
 * It is a pure function `State -> ViewModel` (§6.4): no DOM, no locale, no final
 * strings. It emits message-keys + params (`Str`), semantic design tokens
 * (`SemanticToken`), typed precision-loss objects (`PrecisionLoss`) and a register
 * in {full, plain, kid}. The same `state` + `register` always produce the same
 * `ViewModel`, so snapshots over the §9.1 fixture are stable.
 *
 * The `outside` presentational state (§3.3): a claim whose `needs` base is not all
 * active reads as `outside` — "not even stated under this config". It is derived
 * here from `Verdict.missingBase` and kept visibly distinct from the kernel's
 * `refuted`; it is a UI affordance, never dressed up as a theorem.
 *
 * Register-as-lossy-frame (A7): `plain` and `kid` are lossy frames, so when one is
 * active the builder ALWAYS attaches the corresponding `PrecisionLoss` notes — a
 * light, honest cue, never a gate. `full` attaches none.
 *
 * DECISION (DECISIONS_Agent5.md D2): the frozen §6.4 signature is
 * `(state, register) => ViewModel`, so the builder derives the verdict itself
 * rather than receiving one. It reproduces the §3.3 kernel-port mapping with
 * trivial set membership — `active = (adopted-formal ids) ∪ (imported
 * constraint-edge external ids)`, `refuted ⇔ violates ∩ active ≠ ∅`,
 * `missingBase = needs \ active` — and must NOT import `@progression/core`
 * (INV-DEP: view → contracts only). In production the authoritative verdict is the
 * kernel's, surfaced through Agent 4's `Store.verdictOf`; the presentational
 * derivation here is intentionally the same total function over the same inputs.
 */
import { MSG } from './messages.js';
// --- presentational verdict derivation (mirrors §3.3; no core import) ----------
/**
 * The active config a verdict holds under, derived presentationally:
 * the `adopted-formal` ids, plus the external ids contributed by imported
 * `constraint` edges (only `constraint` edges compose — every other edge kind is
 * inert, §3.3). Resource / heuristic / shared-foundation edges add nothing.
 */
export function deriveActiveConfig(state) {
    const active = new Set();
    for (const [id, status] of state.statusMap) {
        if (status === 'adopted-formal')
            active.add(id);
    }
    for (const edge of state.edges) {
        if (edge.kind === 'constraint' && edge.externalConstraintId !== undefined) {
            active.add(edge.externalConstraintId);
        }
    }
    return active;
}
/**
 * A verdict-shaped object for one claim under `active`. Shapes exactly like the
 * kernel's `Verdict` (§3.4): `conflictingConstraints` are the active ids the claim
 * violates (empty ⇒ explorable); `missingBase` are the `needs` ids not active (the
 * source of the L4 `outside` affordance). `configDigest` here is a stable,
 * order-independent join of the active ids — a presentational stand-in for the
 * kernel digest, used only so equal configs render identically.
 */
export function deriveVerdict(claim, active) {
    const conflicting = [];
    for (const c of claim.violates)
        if (active.has(c))
            conflicting.push(c);
    const missingBase = [];
    for (const n of claim.needs)
        if (!active.has(n))
            missingBase.push(n);
    const state = conflicting.length > 0 ? 'refuted' : 'explorable';
    return {
        state,
        conflictingConstraints: Object.freeze(conflicting),
        missingBase: Object.freeze(missingBase),
        profile: claim.profile,
        configDigest: presentationDigest(active),
    };
}
/** Stable, order-independent digest of an active set (presentational only). */
function presentationDigest(active) {
    return [...active].sort().join('|');
}
export function presentationStateOf(v) {
    if (v.missingBase.length > 0)
        return 'outside';
    return v.state; // 'refuted' | 'explorable'
}
// --- small helpers -------------------------------------------------------------
function str(key, params) {
    return params === undefined ? { key } : { key, params };
}
const STATE_TOKEN = {
    explorable: 'state-explorable',
    refuted: 'state-refuted',
    outside: 'state-outside',
};
const STATE_LABEL = {
    explorable: MSG.stateExplorable,
    refuted: MSG.stateRefuted,
    outside: MSG.stateOutside,
};
const LANE_TOKEN = {
    verified: 'lane-verified',
    unverified: 'lane-unverified',
    'conflict-proven': 'lane-conflict',
};
const LANE_LABEL = {
    verified: MSG.laneVerified,
    unverified: MSG.laneUnverified,
    'conflict-proven': MSG.laneConflict,
};
const EXPERT_LABEL = {
    audited: MSG.expertAudited,
    'in-progress': MSG.expertInProgress,
    'not-reviewed': MSG.expertNotReviewed,
};
const COMMUNITY_LABEL = {
    'flags-exist': MSG.communityFlags,
    none: MSG.communityNone,
};
const STATUS_LABEL = {
    'adopted-formal': MSG.statusAdoptedFormal,
    'adopted-interpretive': MSG.statusAdoptedInterpretive,
    suspended: MSG.statusSuspended,
};
const EDGE_EFFECT = {
    constraint: MSG.edgeEffectConstraint,
    resource: MSG.edgeEffectResource,
    heuristic: MSG.edgeEffectHeuristic,
    'shared-foundation': MSG.edgeEffectShared,
};
/** Join branded ids into a single param string (params are string | number only). */
function joinIds(ids) {
    return ids.join(', ');
}
// --- per-register axis rendering ----------------------------------------------
/**
 * The axis sub-tree for a claim. `full` shows the three axes at full precision;
 * `plain` shows the same three tokens with coarser value keys; `kid` collapses to
 * a single warrant-only hint. The dropped precision is reported as `PrecisionLoss`
 * on the returned nodes for plain/kid (see `registerLossForClaim`).
 */
function axisNodes(profile, register) {
    if (register === 'kid') {
        return [
            {
                token: 'axis-warrant',
                text: str(MSG.axisKidSummary, { w: profile.w }),
            },
        ];
    }
    // `full` and `plain` carry the same three axis tokens and value keys; `plain`
    // differs only in that it also attaches register precision-loss (the catalog is
    // free to phrase the same keys more plainly). `kid` is handled above.
    return [
        { token: 'axis-warrant', text: str(MSG.axisWarrantValue, { w: profile.w }) },
        { token: 'axis-empirical', text: str(MSG.axisEmpiricalValue, { kind: profile.e }) },
        { token: 'axis-modal', text: str(MSG.axisModalValue, { m: profile.m }) },
    ];
}
// --- precision-loss (A3 typed objects; A7 register-as-lossy-frame) -------------
/** The frame-level loss note attached at the root when plain/kid is active. */
function registerFrameLoss(register) {
    if (register === 'full')
        return null;
    const kid = register === 'kid';
    return {
        seamId: `register-frame:${register}`,
        kind: 'register',
        dropped: str(kid ? MSG.lossRegisterKid : MSG.lossRegisterPlain),
        severity: kid ? 'medium' : 'low',
    };
}
/** Per-claim losses introduced by the active register's simplifications. */
function registerLossForClaim(claim, presentation, register) {
    if (register === 'full')
        return [];
    const kid = register === 'kid';
    const losses = [
        {
            seamId: `register:${register}:${claim.id}:profile`,
            kind: 'register',
            dropped: str(MSG.lossProfileDetail),
            severity: kid ? 'medium' : 'low',
        },
    ];
    // kid additionally drops the specific conflicting-constraint detail of a
    // refutation (it just learns "this doesn't work yet"), so note that loss too.
    if (kid && presentation === 'refuted') {
        losses.push({
            seamId: `register:kid:${claim.id}:conflict`,
            kind: 'register',
            dropped: str(MSG.lossConflictDetail),
            severity: 'medium',
        });
    }
    return losses;
}
// --- computational evidence (Compute Integration; shown beside the verdict) ----
/**
 * The Machine-lane token a record reads as — mirrors the proved `lane(Evidence)`:
 * `held` (and not refuted) ⇒ verified; `refutedProof` (and not held) ⇒ conflict;
 * anything else (inconclusive / inconsistent) ⇒ the neutral `unverified`. Evidence
 * never *is* a kernel verdict (INV-CORE); this only colours the evidence line.
 */
function evidenceToken(rec) {
    if (rec.held && !rec.refutedProof)
        return 'lane-verified';
    if (rec.refutedProof && !rec.held)
        return 'lane-conflict';
    return 'lane-unverified';
}
/** Serialize a result payload to a string param (Str.params forbids arrays). */
function serializeResult(result) {
    return Array.isArray(result) ? result.join(', ') : String(result);
}
/**
 * One evidence child node for a record, register-aware via a `level` param so a
 * single key (`evidence.computation`) carries full/plain/kid verbosity (Agent 6's
 * ICU select). All params are always supplied (no missing-arg throws); the catalog
 * chooses what to show per `level`.
 */
function evidenceNode(rec, register) {
    return {
        token: evidenceToken(rec),
        text: str(MSG.evidenceComputation, {
            level: register,
            engine: rec.engine,
            op: rec.op,
            result: serializeResult(rec.result),
            configDigest: rec.configDigest,
        }),
    };
}
/** Losses contributed by one record: a rounding loss when `precision` is present,
 * plus (kid only) a register loss recording that the detail was collapsed. */
function evidenceLosses(rec, register) {
    const losses = [];
    const precision = rec.precision;
    if (precision !== undefined) {
        losses.push({
            seamId: `compute:${rec.claimRef}:${rec.op}:${rec.id}`,
            kind: 'rounding',
            dropped: str(MSG.lossNumericRounding, { decimals: precision.decimals }),
            severity: 'low',
        });
    }
    if (register === 'kid') {
        // kid collapses the evidence to a "checked by computer" cue; record the drop.
        losses.push({
            seamId: `compute:${rec.claimRef}:${rec.op}:${rec.id}:register`,
            kind: 'register',
            dropped: str(MSG.lossRegisterKid),
            severity: 'medium',
        });
    }
    return losses;
}
// --- claim node ----------------------------------------------------------------
function claimNode(claim, active, register, options) {
    // Authoritative kernel verdict when a source is injected (Store.verdictOf);
    // otherwise the pure §3.3 derivation. Either way `outside` reads off missingBase.
    const verdict = options.verdictOf ? options.verdictOf(claim.id) : deriveVerdict(claim, active);
    const presentation = presentationStateOf(verdict);
    const children = [];
    // the three axis nodes (or the kid-register collapse), inlined as claim children
    for (const axis of axisNodes(claim.profile, register))
        children.push(axis);
    // the presentational state label (its own token: explorable/refuted/outside)
    children.push({
        token: STATE_TOKEN[presentation],
        text: str(STATE_LABEL[presentation]),
    });
    // verdict detail: refuted lists the conflicting constraints; outside lists the
    // missing base. kid hides the conflicting-constraint list (reported as a loss).
    if (presentation === 'refuted' && !(register === 'kid')) {
        children.push({
            token: 'state-refuted',
            text: str(MSG.verdictConflict, { constraints: joinIds(verdict.conflictingConstraints) }),
        });
    }
    if (presentation === 'outside') {
        children.push({
            token: 'state-outside',
            text: str(MSG.verdictOutside, { missing: joinIds(verdict.missingBase) }),
        });
    }
    const loss = registerLossForClaim(claim, presentation, register);
    // Computational evidence: one child per record, appended AFTER the verdict so it
    // reads as supporting evidence beside (never as) the kernel verdict. Records are
    // taken in the source's order (log/replay-stable, Deliverable D).
    const records = options.evidenceOf ? options.evidenceOf(claim.id) : [];
    for (const rec of records) {
        children.push(evidenceNode(rec, register));
        for (const l of evidenceLosses(rec, register))
            loss.push(l);
    }
    const node = {
        token: STATE_TOKEN[presentation],
        text: str(claim.labelId), // INV-STABLE-ID: the claim's own key, never a string
        children,
    };
    return loss.length > 0 ? { ...node, loss: Object.freeze(loss) } : node;
}
// --- integrity section (Deliverable B: three lanes, side-by-side, unmerged) ----
/**
 * The three lanes for one System Claim, each on its OWN track with its OWN
 * vocabulary (INV-LANE-1: never merged into a score). Only the Machine lane
 * carries a `lane-*` token; `conflict-proven` is the only state surfaced as a
 * problem (its token is the warning colour). `unverified` is the neutral default,
 * NOT a failure. Expert and Community are presented with neutral tokens and their
 * own label vocabulary so they never read as a machine judgement.
 */
function laneTracks(sc) {
    return [
        {
            token: LANE_TOKEN[sc.lanes.machine], // machine: the only lane with state tokens
            text: str(MSG.laneTrackMachine),
            children: [{ token: LANE_TOKEN[sc.lanes.machine], text: str(LANE_LABEL[sc.lanes.machine]) }],
        },
        {
            token: 'neutral',
            text: str(MSG.laneTrackExpert),
            children: [{ token: 'neutral', text: str(EXPERT_LABEL[sc.lanes.expert]) }],
        },
        {
            token: 'neutral',
            text: str(MSG.laneTrackCommunity),
            children: [{ token: 'neutral', text: str(COMMUNITY_LABEL[sc.lanes.community]) }],
        },
    ];
}
function integrityNode(systemClaims) {
    const children = systemClaims.map((sc) => ({
        // the per-System-Claim node keeps the Machine-lane token as its headline state
        token: LANE_TOKEN[sc.lanes.machine],
        text: str(sc.descriptionId),
        children: laneTracks(sc),
    }));
    return {
        token: 'neutral',
        text: str(MSG.sectionIntegrity),
        children,
    };
}
// --- constraints section (Deliverable A: per-constraint status) ----------------
function constraintsNode(state) {
    const children = [];
    for (const entry of state.constraints.values()) {
        children.push({
            // adopted-interpretive is highlighted with the empirical-axis identity (it is
            // an interpretive choice, the sensitivity exposure tracks); others neutral.
            token: entry.status === 'adopted-interpretive' ? 'axis-empirical' : 'neutral',
            text: str(entry.labelId),
            children: [{ token: 'neutral', text: str(STATUS_LABEL[entry.status]) }],
        });
    }
    return { token: 'neutral', text: str(MSG.sectionConstraints), children };
}
// --- typed-edge section (Deliverable A: each edge's typed effect) --------------
function edgesNode(edges) {
    const children = edges.map((edge) => ({
        // a constraint edge composes (can change verdicts); inert kinds are neutral.
        token: edge.kind === 'constraint' ? 'axis-warrant' : 'neutral',
        text: str(edge.sourceLabelId),
        children: [{ token: 'neutral', text: str(EDGE_EFFECT[edge.kind]) }],
    }));
    return { token: 'neutral', text: str(MSG.sectionEdges), children };
}
// --- frame section (Deliverable A: framing — a frame is a config) --------------
/**
 * The active frame summary. A frame IS a config (§3.1: "frames are configs; same
 * ops"); reframe/compose change the active set, so we surface the active base as
 * the current frame: a count plus the known constraint labels active in it.
 */
function frameNode(state, active) {
    const children = [
        { token: 'neutral', text: str(MSG.frameActive, { count: active.size }) },
    ];
    for (const [id, entry] of state.constraints) {
        if (active.has(id))
            children.push({ token: 'axis-modal', text: str(entry.labelId) });
    }
    return { token: 'neutral', text: str(MSG.sectionFrame), children };
}
// --- exposure section (Deliverable D: adopted-interpretive dependencies) -------
/**
 * The interpretive constraints a claim depends on — the same rule as Agent 4's
 * `Store.exposure` (scan `needs` then `violates`, keep `adopted-interpretive`,
 * de-duplicated in first-seen order). Derived purely from `State` so the view-model
 * stays pure; a host may instead feed `Store.exposure` results — the rule matches.
 */
function exposureOf(state, claim) {
    const seen = new Set();
    const out = [];
    for (const id of [...claim.needs, ...claim.violates]) {
        if (seen.has(id))
            continue;
        seen.add(id);
        if (state.statusMap.get(id) === 'adopted-interpretive')
            out.push(id);
    }
    return out;
}
function exposureNode(state) {
    const children = [];
    for (const claim of state.claims.values()) {
        const deps = exposureOf(state, claim);
        const depNodes = deps.length > 0
            ? deps.map((id) => ({ token: 'axis-empirical', text: str(MSG.exposureDep, { id }) }))
            : [{ token: 'neutral', text: str(MSG.exposureNone) }];
        children.push({ token: 'neutral', text: str(claim.labelId), children: depNodes });
    }
    return { token: 'neutral', text: str(MSG.sectionExposure), children };
}
// --- aggregation ---------------------------------------------------------------
/** Depth-first collection of every `PrecisionLoss` carried on the tree. */
function collectLoss(node, into) {
    if (node.loss)
        for (const l of node.loss)
            into.push(l);
    if (node.children)
        for (const c of node.children)
            collectLoss(c, into);
}
// --- the builder ---------------------------------------------------------------
/**
 * Build the locale-neutral view-model for a state under a register. Pure.
 *
 * Section order is backward-compatible with Phase 1: `root.children[0]` is the
 * claims section and `[1]` is the integrity section (Phase 1 tests index these);
 * Phase 2 appends constraints, edges, frame and exposure sections at `[2..]`.
 */
export function buildViewModel(state, register, options = {}) {
    const active = deriveActiveConfig(state);
    // claims in a stable order (the map's insertion order is the event order).
    const claimChildren = [];
    for (const claim of state.claims.values()) {
        claimChildren.push(claimNode(claim, active, register, options));
    }
    const claimsSection = {
        token: 'neutral',
        text: str(MSG.sectionClaims),
        children: claimChildren,
    };
    const systemClaims = [...state.integrity.values()];
    const integritySection = integrityNode(systemClaims);
    const frameLoss = registerFrameLoss(register);
    const root = {
        token: 'neutral',
        text: str(MSG.inquiryTitle),
        children: [
            claimsSection, // [0] — Phase 1
            integritySection, // [1] — Phase 1
            constraintsNode(state), // [2] — Phase 2: per-constraint status
            edgesNode(state.edges), // [3] — Phase 2: typed-edge effects
            frameNode(state, active), // [4] — Phase 2: the active frame (config)
            exposureNode(state), // [5] — Phase 2: interpretive exposure
        ],
        ...(frameLoss ? { loss: Object.freeze([frameLoss]) } : {}),
    };
    // aggregate every loss on the tree (frame + per-claim) for the integrity
    // registry to index (Deliverable D). Order is deterministic: root-first, DFS.
    const aggregated = [];
    collectLoss(root, aggregated);
    return Object.freeze({
        register,
        dirHint: 'auto',
        root,
        precisionLoss: Object.freeze(aggregated),
    });
}
//# sourceMappingURL=view-model.js.map